import React from "react";

import { apiGet } from "../../api";

// Data + filter-state controller for the /video-pembelajaran listing page. GETs
// the public /api/v1/video_pembelajaran catalog, mirrors the rebrand pagination
// envelope ({ data, meta: { page, pages, perpage, total } }), and keeps the
// active filters in the URL query string so a filtered view is shareable and
// survives back/forward navigation.
//
// Modelled on useWebinarsIndex. The endpoint whitelists every param
// server-side; this hook only ever sends the sanitized shapes below.
//
// `learning_name` is the one filter that also arrives from outside the page —
// the homepage group titles link straight to
// /video-pembelajaran?learning_name=<slug> — so it has to round-trip through
// the URL, not just through component state.

const PER_PAGE = 12;

const SORTS = ["newest", "oldest", "name_asc"];
const ACCESS = ["free", "subscription"];
const WATCHED = ["unwatched", "in_progress", "completed"];

export const DEFAULT_FILTERS = {
  q: "",
  // Nama Materi Belajar Pajak — the managed vocabulary. Value is a slug,
  // validated server-side against the vocabulary table.
  learning_name: "",
  category: "",
  access: "",
  watched: "",
  sort: "newest",
  page: 1,
};

function toUrlParams(filters) {
  const p = new URLSearchParams();
  if (filters.q) p.set("q", filters.q);
  if (filters.learning_name) p.set("learning_name", filters.learning_name);
  if (filters.category) p.set("category", filters.category);
  if (filters.access) p.set("access", filters.access);
  if (filters.watched) p.set("watched", filters.watched);
  if (filters.sort && filters.sort !== "newest") p.set("sort", filters.sort);
  if (filters.page && filters.page > 1) p.set("page", String(filters.page));
  return p;
}

function fromUrl() {
  if (typeof window === "undefined") return { ...DEFAULT_FILTERS };
  const p = new URLSearchParams(window.location.search);
  const page = parseInt(p.get("page"), 10);
  return {
    q: p.get("q") || "",
    // Slugs are free-form by nature; the server ignores unknown ones rather than
    // returning an empty page, and the sidebar simply highlights nothing.
    learning_name: p.get("learning_name") || "",
    category: p.get("category") || "",
    access: ACCESS.includes(p.get("access")) ? p.get("access") : "",
    watched: WATCHED.includes(p.get("watched")) ? p.get("watched") : "",
    sort: SORTS.includes(p.get("sort")) ? p.get("sort") : "newest",
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

function syncUrl(filters) {
  if (typeof window === "undefined") return;
  const qs = toUrlParams(filters).toString();
  const next = qs
    ? `${window.location.pathname}?${qs}`
    : window.location.pathname;
  window.history.replaceState(null, "", next);
}

export default function useVideoPembelajaranIndex() {
  const [filters, setFilters] = React.useState(fromUrl);
  const [results, setResults] = React.useState([]);
  const [meta, setMeta] = React.useState({
    page: 1,
    pages: 0,
    perpage: PER_PAGE,
    total: 0,
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  // `filters.q` only ever holds a COMMITTED search term, so this effect needs no
  // debounce of its own. Every filter change here is a single deliberate action
  // (the sidebar, a radio, a page) and applies immediately; typing is debounced
  // upstream in useDebouncedSearch.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    syncUrl(filters);

    const run = async () => {
      const params = toUrlParams(filters);
      params.set("per_page", String(PER_PAGE));
      try {
        const { parsedResponse, statusCode } = await apiGet(
          `/api/v1/video_pembelajaran?${params.toString()}`,
        );
        if (cancelled) return;
        if (statusCode >= 400 || !parsedResponse) {
          setError(true);
          setResults([]);
          setMeta((m) => ({ ...m, pages: 0, total: 0 }));
        } else {
          setResults(parsedResponse.data ?? []);
          setMeta(
            parsedResponse.meta ?? {
              page: filters.page,
              pages: 0,
              perpage: PER_PAGE,
              total: 0,
            },
          );
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setResults([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  // Any filter change resets to page 1; paging keeps the rest intact.
  const update = React.useCallback((patch) => {
    setFilters((f) => ({ ...f, ...patch, page: 1 }));
  }, []);

  // Handed to <SearchBox>, which owns the draft. Stable identity is load-bearing:
  // SearchBox rebuilds its debounce timer whenever this changes.
  const commitSearch = React.useCallback((q) => update({ q }), [update]);

  const setPage = React.useCallback((page) => {
    setFilters((f) => ({ ...f, page }));
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  // Bumped on reset so <SearchBox> clears its own draft. The parent deliberately
  // does not hold the draft, so it cannot clear it directly.
  const [searchResetToken, setSearchResetToken] = React.useState(0);

  const reset = React.useCallback(() => {
    setSearchResetToken((n) => n + 1);
    setFilters({ ...DEFAULT_FILTERS });
  }, []);

  const activeFilterCount = React.useMemo(() => {
    let n = 0;
    if (filters.q) n += 1;
    if (filters.learning_name) n += 1;
    if (filters.category) n += 1;
    if (filters.access) n += 1;
    if (filters.watched) n += 1;
    if (filters.sort !== "newest") n += 1;
    return n;
  }, [filters]);

  return {
    filters,
    results,
    meta,
    loading,
    error,
    update,
    setPage,
    reset,
    activeFilterCount,
    perPage: PER_PAGE,
    commitSearch,
    searchResetToken,
  };
}
